import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Award, Plus, Search, Printer, Trash2, Loader2, Eye,
  FileText, Heart, Briefcase, Star, User, Calendar,
} from 'lucide-react';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const CERT_TYPES = [
  { value: 'fitness',             label: 'Fitness Certificate',     icon: Heart,    color: 'text-green-600 bg-green-50' },
  { value: 'sick_leave',          label: 'Sick Leave Certificate',  icon: Briefcase,color: 'text-blue-600 bg-blue-50' },
  { value: 'medical_examination', label: 'Medical Examination',      icon: FileText, color: 'text-purple-600 bg-purple-50' },
  { value: 'death',               label: 'Death Certificate',       icon: FileText, color: 'text-gray-600 bg-gray-50' },
  { value: 'disability',          label: 'Disability Certificate',  icon: Star,     color: 'text-orange-600 bg-orange-50' },
  { value: 'referral',            label: 'Referral Letter',         icon: FileText, color: 'text-teal-600 bg-teal-50' },
  { value: 'other',               label: 'Other Certificate',       icon: Award,    color: 'text-navy bg-navy/5' },
];

function CertTypeCard({ type, selected, onClick }: any) {
  const cfg = CERT_TYPES.find(t => t.value === type.value) ?? CERT_TYPES[CERT_TYPES.length - 1];
  const Icon = cfg.icon;
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex flex-col items-center gap-2 p-3 rounded-xl border-2 text-center transition-all',
        selected
          ? 'border-orange bg-orange/5 shadow-sm'
          : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
      )}>
      <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center', cfg.color)}>
        <Icon size={17} />
      </div>
      <span className="text-xs font-medium text-navy leading-tight">{type.label}</span>
    </button>
  );
}

function NewCertModal({ onClose }: any) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'type' | 'patient' | 'form'>('type');
  const [certType, setCertType] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [form, setForm] = useState<any>({
    issue_date: format(new Date(), 'yyyy-MM-dd'),
    fitness_status: 'fit',
    leave_days: 1,
    leave_from_date: format(new Date(), 'yyyy-MM-dd'),
    leave_to_date: format(new Date(), 'yyyy-MM-dd'),
    death_place: 'hospital',
  });

  const { data: patients } = useQuery({
    queryKey: ['patient-search-cert', patientSearch],
    queryFn: () => api.get('/patients', { params: { search: patientSearch, limit: 8 } }).then(r => r.data),
    enabled: patientSearch.length > 1,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/certificates', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['certificates'] }); onClose(); },
  });

  function setField(k: string, v: any) { setForm((f: any) => ({ ...f, [k]: v })); }

  function submit() {
    createMutation.mutate({
      patient_id: selectedPatient.id,
      cert_type: certType,
      ...form,
      leave_days: certType === 'sick_leave' ? Number(form.leave_days) : undefined,
    });
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-base font-bold text-navy flex items-center gap-2">
            <Award size={16} className="text-orange" />
            {step === 'type' ? 'Select Certificate Type' : step === 'patient' ? 'Select Patient' : 'Certificate Details'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-light leading-none">×</button>
        </div>

        <div className="p-5">
          {step === 'type' && (
            <div className="grid grid-cols-3 gap-3">
              {CERT_TYPES.map(t => (
                <CertTypeCard
                  key={t.value}
                  type={t}
                  selected={certType === t.value}
                  onClick={() => { setCertType(t.value); setStep('patient'); }}
                />
              ))}
            </div>
          )}

          {step === 'patient' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Certificate type: <span className="font-semibold text-navy">{CERT_TYPES.find(t => t.value === certType)?.label}</span>
              </p>
              {selectedPatient ? (
                <div className="flex items-center justify-between bg-orange/5 border border-orange/20 rounded-xl px-4 py-3">
                  <div>
                    <p className="font-semibold text-navy">{selectedPatient.name}</p>
                    <p className="text-xs text-gray-500">{selectedPatient.patient_uid} · {selectedPatient.age_years}y {selectedPatient.gender}</p>
                  </div>
                  <button onClick={() => setSelectedPatient(null)} className="text-xs text-gray-400 hover:text-red-500">Change</button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    value={patientSearch}
                    onChange={e => setPatientSearch(e.target.value)}
                    placeholder="Search patient by name or UHID..."
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange/30 focus:border-orange outline-none"
                  />
                  {patients?.data?.length > 0 && (
                    <div className="absolute z-10 top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl mt-1 max-h-52 overflow-y-auto">
                      {patients.data.map((p: any) => (
                        <button key={p.id} onClick={() => { setSelectedPatient(p); setPatientSearch(''); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-orange/5 text-sm border-b border-gray-50 last:border-0">
                          <span className="font-medium text-navy">{p.name}</span>
                          <span className="text-gray-400 ml-2 text-xs">{p.patient_uid}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep('type')} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">Back</button>
                <button
                  onClick={() => setStep('form')}
                  disabled={!selectedPatient}
                  className="flex-1 py-2 bg-orange text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-orange/90 transition">
                  Next
                </button>
              </div>
            </div>
          )}

          {step === 'form' && (
            <div className="space-y-4">
              <div className="bg-orange/5 border border-orange/15 rounded-xl px-4 py-2.5 text-sm">
                <span className="text-gray-500">Patient: </span>
                <span className="font-semibold text-navy">{selectedPatient?.name}</span>
                <span className="text-gray-400 ml-2">{selectedPatient?.patient_uid}</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Issue Date *</label>
                  <input type="date" value={form.issue_date} onChange={e => setField('issue_date', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
                </div>
                {certType !== 'death' && (
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Valid Until</label>
                    <input type="date" value={form.valid_until ?? ''} onChange={e => setField('valid_until', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
                  </div>
                )}
              </div>

              {certType === 'fitness' && (
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Fitness Status *</label>
                  <select value={form.fitness_status} onChange={e => setField('fitness_status', e.target.value)}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                    <option value="fit">Fit</option>
                    <option value="fit_with_restrictions">Fit with Restrictions</option>
                    <option value="temporarily_unfit">Temporarily Unfit</option>
                    <option value="unfit">Unfit</option>
                  </select>
                </div>
              )}

              {certType === 'sick_leave' && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">From Date</label>
                    <input type="date" value={form.leave_from_date} onChange={e => setField('leave_from_date', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">To Date</label>
                    <input type="date" value={form.leave_to_date} onChange={e => setField('leave_to_date', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Days</label>
                    <input type="number" min="1" value={form.leave_days} onChange={e => setField('leave_days', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
                  </div>
                </div>
              )}

              {certType === 'death' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Date of Death</label>
                    <input type="date" value={form.death_date ?? ''} onChange={e => setField('death_date', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Time of Death</label>
                    <input type="time" value={form.death_time ?? ''} onChange={e => setField('death_time', e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs font-semibold text-gray-600 block mb-1">Cause of Death</label>
                    <input type="text" value={form.death_cause ?? ''} onChange={e => setField('death_cause', e.target.value)}
                      placeholder="e.g. Cardio-respiratory failure due to..."
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Diagnosis / Clinical Findings</label>
                <input type="text" value={form.diagnosis ?? ''} onChange={e => setField('diagnosis', e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">
                  {certType === 'referral' ? 'Reason for Referral' : certType === 'fitness' ? 'Restrictions / Conditions' : 'Recommendations / Advice'}
                </label>
                <textarea value={form.recommendation ?? ''} onChange={e => setField('recommendation', e.target.value)}
                  rows={3} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none resize-none" />
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={() => setStep('patient')} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">Back</button>
                <button
                  onClick={submit}
                  disabled={createMutation.isPending}
                  className="flex-1 py-2 bg-orange text-white rounded-xl text-sm font-semibold disabled:opacity-50 hover:bg-orange/90 flex items-center justify-center gap-2 transition">
                  {createMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                  Issue Certificate
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CertificatesPage() {
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['certificates', typeFilter],
    queryFn: () => api.get('/certificates', { params: { cert_type: typeFilter || undefined, limit: 100 } }).then(r => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/certificates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['certificates'] }),
  });

  const certs = (data?.certificates ?? []).filter((c: any) =>
    !search || c.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.patient_uid?.toLowerCase().includes(search.toLowerCase())
  );

  function printCert(id: string) {
    const url = `${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/api/v1/certificates/${id}/html`;
    window.open(url, '_blank');
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <Award size={22} className="text-orange" /> Medical Certificates
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Issue fitness, sick leave, referral and other medical certificates</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90 transition shadow-sm">
          <Plus size={15} /> Issue Certificate
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 flex-1 min-w-48 max-w-xs">
          <Search size={13} className="text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search patient..."
            className="bg-transparent text-sm outline-none flex-1 placeholder:text-gray-400" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-orange/30">
          <option value="">All Types</option>
          {CERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={22} className="animate-spin text-orange" />
          </div>
        ) : certs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Award size={40} className="mb-3 opacity-25" />
            <p className="text-base font-medium text-navy">No certificates issued yet</p>
            <button onClick={() => setShowNew(true)}
              className="mt-3 text-sm text-orange hover:underline flex items-center gap-1">
              <Plus size={13} /> Issue your first certificate
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cert No.</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Patient</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Doctor</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c: any) => {
                const typeCfg = CERT_TYPES.find(t => t.value === c.cert_type);
                return (
                  <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{c.cert_number}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-navy">{c.patient_name}</p>
                      <p className="text-xs text-gray-400">{c.patient_uid}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={clsx('text-xs px-2 py-1 rounded-full font-medium', typeCfg?.color ?? 'bg-gray-50 text-gray-600')}>
                        {typeCfg?.label ?? c.cert_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">Dr. {c.doctor_name}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {c.issue_date ? format(new Date(c.issue_date), 'dd MMM yyyy') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => printCert(c.id)}
                          className="p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 rounded-lg transition"
                          title="View & Print">
                          <Printer size={14} />
                        </button>
                        <button
                          onClick={() => { if (confirm('Delete this certificate?')) deleteMutation.mutate(c.id); }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <NewCertModal onClose={() => setShowNew(false)} />}
    </div>
  );
}
